import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import {
  ArrowRight,
  Brain,
  Users,
  ShieldCheck,
  GitMerge,
  ListChecks,
  Megaphone,
  FileText,
  TrendingUp,
  Activity,
  Volume2,
  Scale,
  Sparkles,
  CheckCircle2,
  XCircle,
  Workflow,
  PlayCircle,
  History,
  LogIn,
} from "lucide-react";

const WORKFLOW = [
  { label: "Raise Incident", icon: PlayCircle },
  { label: "Dispatch Agents", icon: Workflow },
  { label: "Check Pipeline Progress", icon: Activity },
  { label: "Consensus View", icon: GitMerge },
  { label: "Report Generated", icon: FileText },
  { label: "Human Approval", icon: ShieldCheck },
];

const WHY_DIFFERENT = [
  {
    title: "Reduce cross-team friction",
    desc: "Replace fragmented bridge calls with a shared reasoning view that brings every domain to the same conclusion faster.",
    icon: Users,
  },
  {
    title: "Shared reasoning view",
    desc: "Specialist agents publish their hypotheses, evidence, and confidence side-by-side so on-calls debate signals, not opinions.",
    icon: Brain,
  },
  {
    title: "Human approval & governance",
    desc: "Every recommended action is gated by an explicit human decision and recorded in an immutable audit trail.",
    icon: ShieldCheck,
  },
];

const HELPS = [
  {
    title: "Cross-Team Incident Reasoning",
    desc: "Convert scattered incident details into a clear, structured narrative every team can act on.",
    icon: GitMerge,
  },
  {
    title: "Multi-Agent RCA Hypotheses",
    desc: "Specialist agents propose likely root cause hypotheses, ranked by weighted consensus.",
    icon: Brain,
  },
  {
    title: "Ranked Safe Actions",
    desc: "Prioritised, human-reviewable next steps that highlight blast radius and rollback posture.",
    icon: ListChecks,
  },
  {
    title: "Executive & Technical Updates",
    desc: "One concise narrative — tuned for leadership and engineers — straight from the reasoning trail.",
    icon: Megaphone,
  },
  {
    title: "Human Approval & Governance",
    desc: "Every recommendation requires explicit reviewer approval. Decisions are logged with reasoning context.",
    icon: ShieldCheck,
  },
  {
    title: "Human-Reviewed Runbook Drafts",
    desc: "Generate a categorised runbook draft for response, recovery, and follow-up — reviewed before execution.",
    icon: FileText,
  },
];

const BENEFITS = [
  {
    title: "Faster triage",
    desc: "Cut time-to-hypothesis with parallelised domain agents.",
    icon: TrendingUp,
  },
  {
    title: "Higher team productivity",
    desc: "On-calls focus on validation, not synthesis.",
    icon: Activity,
  },
  {
    title: "Lower bridge noise",
    desc: "Shared reasoning replaces cross-team back-and-forth.",
    icon: Volume2,
  },
  {
    title: "Stronger governance",
    desc: "Decisions, approvals, and overrides — all auditable.",
    icon: Scale,
  },
];

const TRADITIONAL = [
  "Capture tickets",
  "Track updates",
  "Manage workflow",
  "Record incident history",
];

const CRE_LAYER = [
  "Dispatches specialist reasoning agents",
  "Compares cross-team hypotheses",
  "Consolidates likely RCA signals",
  "Recommends safe human-reviewable actions",
  "Captures governance and approval context",
];

const AUDIENCES = [
  "IT Operations",
  "SRE Teams",
  "Incident Managers",
  "Problem Managers",
  "Service Delivery",
  "MSPs",
  "Application Support",
  "Infrastructure Teams",
  "Network Teams",
  "Security Teams",
  "Database Teams",
  "Management Stakeholders",
];

function GradientIconBadge({ Icon }) {
  return (
    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-sky-500/10 via-cyan-500/10 to-violet-500/10 border border-sky-200 flex items-center justify-center">
      <Icon className="h-4.5 w-4.5 text-sky-600" strokeWidth={1.75} />
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 cre-hero-aurora pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-8 md:pt-16 pb-10 md:pb-14">
          <div
            data-testid="hero-availability"
            className="cre-pill cre-pill-grad"
          >
            <Sparkles className="h-3 w-3" strokeWidth={2} />
            Patent-backed AI Decision Intelligence
          </div>

          <h1
            data-testid="hero-heading"
            className="mt-5 md:mt-6 font-heading font-black tracking-tighter text-3xl sm:text-5xl lg:text-6xl leading-[1.06] max-w-4xl"
          >
            <span>AI-powered </span>
            <span className="cre-grad-text">Decision Intelligence</span>
            <span> for major incident response</span>
          </h1>

          <p
            data-testid="hero-subheading"
            className="mt-4 md:mt-5 text-sm sm:text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl"
          >
            CRE — Collaborative Resolution Environment is an AI incident
            reasoning workspace for IT operations. It helps during major
            incidents where symptoms are fragmented, RCA thinking is delayed,
            war rooms become noisy, and decisions slow down.
          </p>

          <div className="mt-6 md:mt-8 flex items-center gap-3 flex-wrap">
            <Link to="/incidents/new" data-testid="hero-cta-primary">
              <Button
                size="lg"
                className="cre-cta-primary rounded-lg font-semibold px-5 h-11"
              >
                Raise New Incident
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
              </Button>
            </Link>
            <Link to="/dashboard" data-testid="hero-cta-secondary">
              <Button
                size="lg"
                variant="outline"
                className="rounded-lg border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-semibold px-5 h-11"
              >
                <Brain className="mr-2 h-4 w-4 text-sky-600" strokeWidth={2} />
                View Reasoning Workspace
              </Button>
            </Link>
            <Link to="/login" data-testid="hero-cta-signin">
              <Button
                size="lg"
                variant="outline"
                className="rounded-lg border-sky-300 text-sky-700 hover:bg-sky-50 hover:text-sky-800 font-semibold px-5 h-11"
              >
                <LogIn className="mr-2 h-4 w-4" strokeWidth={2} />
                Sign In to Save History
              </Button>
            </Link>
            <Link
              to="/dashboard"
              data-testid="hero-cta-tertiary"
              className="ml-1 text-sm text-slate-600 hover:text-sky-700 transition-colors underline-offset-4 hover:underline inline-flex items-center gap-1"
            >
              <History className="h-4 w-4" strokeWidth={1.75} />
              Browse Recent Analyses →
            </Link>
          </div>

          <div className="mt-3">
            <Link
              to="/preview"
              data-testid="hero-cta-preview"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-800 transition-colors underline-offset-4 hover:underline"
            >
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
              Or try CRE without login →
            </Link>
          </div>

          <p
            data-testid="hero-detail-domains"
            className="mt-6 md:mt-7 text-sm sm:text-base md:text-[15px] text-slate-600 leading-relaxed max-w-3xl"
          >
            CRE dispatches domain-specific reasoning agents across application,
            infrastructure, network, database, security, and service-impact
            areas. It produces likely{" "}
            <Link
              to="/root-cause-analysis-ai"
              data-testid="hero-inline-link-rca"
              className="text-sky-700 hover:text-sky-800 underline-offset-4 hover:underline"
            >
              RCA hypotheses
            </Link>
            , safe recommended actions, stakeholder updates, and human-approved
            runbooks for{" "}
            <Link
              to="/major-incident-management"
              data-testid="hero-inline-link-mim"
              className="text-sky-700 hover:text-sky-800 underline-offset-4 hover:underline"
            >
              major incident management
            </Link>
            .
          </p>

          <p
            data-testid="hero-detail-trust"
            className="mt-2.5 md:mt-3 text-[13px] sm:text-sm md:text-[15px] text-slate-500 leading-relaxed max-w-3xl"
          >
            CRE does not replace ITSM or observability tools. It complements
            them as a decision intelligence layer, helping teams reason faster
            during critical and business-impacting incidents. It does not
            execute changes automatically. Human approval remains mandatory.
          </p>

          <p
            data-testid="hero-signin-note"
            className="mt-3 text-[12px] sm:text-[13px] text-slate-500 max-w-2xl"
          >
            Sign in to save incident history, revisit previous analyses, and
            track human-reviewed outputs.
          </p>

          <div
            data-testid="governance-note"
            className="mt-6 inline-flex items-center gap-2 text-[12px] text-slate-500 font-mono"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
            Use anonymized incident inputs only. Human approval required.
          </div>

          {/* Workflow stepper */}
          <div
            data-testid="workflow-stepper"
            className="mt-7 md:mt-10 cre-card p-3 sm:p-5 max-w-5xl"
          >
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="cre-overline text-slate-500">How CRE works</div>
              <div className="text-[10px] font-mono text-slate-400 sm:hidden">
                swipe →
              </div>
            </div>
            <div
              className="flex items-start gap-1 sm:gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {WORKFLOW.map((w, i) => {
                const Icon = w.icon;
                return (
                  <div
                    key={w.label}
                    data-testid={`workflow-step-${i + 1}`}
                    className="flex items-start gap-1 sm:gap-2 shrink-0 snap-start"
                  >
                    <div className="flex flex-col items-center text-center min-w-[88px] sm:min-w-[120px] max-w-[110px] sm:max-w-[140px]">
                      <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-sky-500/10 via-cyan-500/10 to-violet-500/10 border border-sky-200 flex items-center justify-center">
                        <Icon
                          className="h-4 w-4 text-sky-600"
                          strokeWidth={1.75}
                        />
                      </div>
                      <div className="mt-1.5 text-[10.5px] sm:text-xs font-semibold text-slate-700 leading-tight px-0.5 break-words">
                        {w.label}
                      </div>
                    </div>
                    {i < WORKFLOW.length - 1 ? (
                      <ArrowRight
                        className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-3"
                        strokeWidth={2}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* WHY CRE IS DIFFERENT */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
          <div className="max-w-3xl">
            <div className="cre-overline text-sky-700">Why CRE is different</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              Decision intelligence, not another ticketing tool
            </h2>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
              CRE sits beside the tools you already use and adds a reasoning
              layer on top of them — so on-call engineers, incident managers,
              and leadership all see the same picture.
            </p>
          </div>

          <div className="mt-7 md:mt-10 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {WHY_DIFFERENT.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  data-testid={`why-card-${c.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                  className="cre-card p-4 md:p-6"
                >
                  <GradientIconBadge Icon={Icon} />
                  <div className="mt-3 md:mt-4 font-heading font-bold tracking-tight text-slate-900">
                    {c.title}
                  </div>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                    {c.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT CRE HELPS TEAMS DO */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
          <div className="max-w-3xl">
            <div className="cre-overline text-sky-700">What CRE helps teams do</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              Turn incident chatter into decisions you can act on
            </h2>
          </div>

          <div className="mt-7 md:mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {HELPS.map((h) => {
              const Icon = h.icon;
              return (
                <div
                  key={h.title}
                  data-testid={`help-card-${h.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                  className="cre-card p-4 md:p-6"
                >
                  <GradientIconBadge Icon={Icon} />
                  <div className="mt-3 md:mt-4 font-heading font-bold tracking-tight text-slate-900">
                    {h.title}
                  </div>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                    {h.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BUSINESS BENEFITS */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
          <div className="max-w-3xl">
            <div className="cre-overline text-sky-700">Business benefits</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              Faster triage. Quieter bridges. Stronger governance.
            </h2>
          </div>

          <div className="mt-7 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  data-testid={`benefit-card-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="cre-card p-4 md:p-5"
                >
                  <GradientIconBadge Icon={Icon} />
                  <div className="mt-3 md:mt-4 font-heading font-bold tracking-tight text-slate-900 text-sm md:text-base">
                    {b.title}
                  </div>
                  <p className="mt-1 text-xs sm:text-sm text-slate-600 leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NOT ANOTHER INCIDENT TOOL — comparison */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
          <div className="max-w-3xl">
            <div className="cre-overline text-sky-700">Not another incident tool</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              CRE complements your incident stack — it doesn’t replace it
            </h2>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
              Ticketing and on-call tools capture <em>what</em> happened. CRE
              focuses on <em>why</em> it happened and <em>what to do next</em>
              {" "}— bringing structured reasoning to{" "}
              <Link
                to="/ai-incident-management"
                data-testid="comparison-inline-link-aim"
                className="text-sky-700 hover:text-sky-800 underline-offset-4 hover:underline"
              >
                AI incident management
              </Link>
              ,{" "}
              <Link
                to="/aiops-incident-reasoning"
                data-testid="comparison-inline-link-aiops"
                className="text-sky-700 hover:text-sky-800 underline-offset-4 hover:underline"
              >
                AIOps incident reasoning
              </Link>
              , and{" "}
              <Link
                to="/it-downtime-reduction"
                data-testid="comparison-inline-link-downtime"
                className="text-sky-700 hover:text-sky-800 underline-offset-4 hover:underline"
              >
                IT downtime reduction
              </Link>
              .
            </p>
          </div>

          <div className="mt-7 md:mt-10 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
            <div
              data-testid="comparison-traditional"
              className="cre-card p-4 md:p-6"
            >
              <div className="flex items-center gap-2 cre-overline text-slate-500">
                <XCircle className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
                Traditional incident tools
              </div>
              <ul className="mt-3 md:mt-4 space-y-2 md:space-y-2.5">
                {TRADITIONAL.map((t) => (
                  <li
                    key={t}
                    className="flex items-start gap-2 text-sm text-slate-700"
                  >
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-slate-400 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              data-testid="comparison-cre"
              className="cre-card p-4 md:p-6 border-sky-200 bg-gradient-to-br from-white to-sky-50/40"
            >
              <div className="flex items-center gap-2 cre-overline text-sky-700">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
                CRE reasoning layer
              </div>
              <ul className="mt-3 md:mt-4 space-y-2 md:space-y-2.5">
                {CRE_LAYER.map((t) => (
                  <li
                    key={t}
                    className="flex items-start gap-2 text-sm text-slate-800"
                  >
                    <CheckCircle2
                      className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0"
                      strokeWidth={2}
                    />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* EXPLORE BY USE CASE — internal links for SEO crawl depth */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
          <div className="max-w-3xl">
            <div className="cre-overline text-sky-700">Explore by use case</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              Where CRE fits in your incident operating model
            </h2>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
              CRE supports the moments where structured reasoning matters most —
              from triage to recovery and post-incident review.
            </p>
          </div>

          <div
            data-testid="use-case-grid"
            className="mt-7 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
          >
            {[
              {
                to: "/ai-incident-management",
                title: "AI Incident Management",
                desc: "Multi-agent reasoning across app, infra, network, security, database, and business impact.",
                testid: "use-case-card-ai-incident-management",
              },
              {
                to: "/aiops-incident-reasoning",
                title: "AIOps Incident Reasoning",
                desc: "Turn AIOps signals into ranked hypotheses your team can act on.",
                testid: "use-case-card-aiops-incident-reasoning",
              },
              {
                to: "/root-cause-analysis-ai",
                title: "Root Cause Analysis AI",
                desc: "Live RCA hypotheses with supporting reasoning — not just post-mortem write-ups.",
                testid: "use-case-card-root-cause-analysis-ai",
              },
              {
                to: "/major-incident-management",
                title: "Major Incident Management",
                desc: "Quieter bridges, aligned communication, approval-ready runbooks during sev-1s.",
                testid: "use-case-card-major-incident-management",
              },
              {
                to: "/it-downtime-reduction",
                title: "IT Downtime Reduction",
                desc: "Compress reasoning time without weakening change governance.",
                testid: "use-case-card-it-downtime-reduction",
              },
              {
                to: "/demo",
                title: "See CRE in action",
                desc: "Run an anonymized incident through CRE — no signup, no setup.",
                testid: "use-case-card-demo",
              },
            ].map((u) => (
              <Link
                key={u.to}
                to={u.to}
                data-testid={u.testid}
                className="cre-card p-4 md:p-5 group hover:border-sky-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="font-heading font-bold tracking-tight text-slate-900 text-sm md:text-base">
                    {u.title}
                  </div>
                  <ArrowRight
                    className="h-4 w-4 text-slate-400 group-hover:text-sky-600 mt-0.5 shrink-0 transition-colors"
                    strokeWidth={2}
                  />
                </div>
                <p className="mt-1.5 text-[13px] sm:text-sm text-slate-600 leading-relaxed">
                  {u.desc}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BUILT FOR MODERN INCIDENT ENVIRONMENTS */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
          <div className="max-w-3xl">
            <div className="cre-overline text-sky-700">Built for modern incident environments</div>
            <h2 className="mt-2 font-heading font-black tracking-tighter text-2xl md:text-4xl text-slate-900">
              One reasoning layer for every team that touches an incident
            </h2>
          </div>

          <div
            data-testid="audience-badges"
            className="mt-6 flex flex-wrap gap-1.5 sm:gap-2"
          >
            {AUDIENCES.map((a) => (
              <span
                key={a}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:text-sky-700 transition-colors"
              >
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
